import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { MOCK_ENTITIES, MOCK_NUMBERING } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { IssuingEntity, NumberedRecordKind, NumberingScheme, Office } from '@/types/domain'

/* Company settings — Stories 1.5 and 1.6.
 *
 * These endpoints live under /admin and are refused server-side for every role
 * but Super-admin (FR-56, AD-5). They are the entities every document is
 * printed from and the patterns that name every record.
 *
 * Bank details travel on this shape and on no other: the server serves them
 * only to this screen, and every other surface receives an entity reference
 * without them (AD-6).
 */

const entityKeys = {
  all: ['settings', 'entities'] as const,
  numbering: ['settings', 'numbering'] as const,
}

export interface EntityPayload {
  name: string
  initials: string
  officeCode: string
  defaultOffice: Office | ''
  legalFooter: string
  bankDetails: string
  emailSignature: string
  logoVariant: string
}

export function useIssuingEntities() {
  return useQuery<IssuingEntity[]>({
    queryKey: entityKeys.all,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_ENTITIES
        : (await api.get<{ items: IssuingEntity[] }>('/admin/entities')).items,
  })
}

export function useNumberingSchemes() {
  return useQuery<NumberingScheme[]>({
    queryKey: entityKeys.numbering,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_NUMBERING
        : (await api.get<{ items: NumberingScheme[] }>('/admin/numbering')).items,
  })
}

/** Invalidates both lists after any change.
 *
 * Numbering is invalidated alongside entities because a scheme's live example
 * is rendered from the entity's initials and office code — renaming `AB` to
 * `ABG` changes every example on the numbering tab. */
function useInvalidateSettings() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: entityKeys.all })
    void queryClient.invalidateQueries({ queryKey: entityKeys.numbering })
  }
}

export function useCreateEntity() {
  const invalidate = useInvalidateSettings()

  return useMutation<IssuingEntity, Error, EntityPayload>({
    mutationFn: (payload) => api.post<IssuingEntity>('/admin/entities', payload),
    onSuccess: invalidate,
  })
}

/** Amends an entity.
 *
 * The whole record is submitted rather than a patch: a nil-means-unchanged body
 * would make "clear the bank details" indistinguishable from "leave them
 * alone". */
export function useUpdateEntity() {
  const invalidate = useInvalidateSettings()

  return useMutation<IssuingEntity, Error, { id: string; payload: EntityPayload }>({
    mutationFn: ({ id, payload }) => api.put<IssuingEntity>(`/admin/entities/${id}`, payload),
    onSuccess: invalidate,
  })
}

/** Activates or deactivates an entity.
 *
 * Deactivation rather than deletion: an entity that has issued a document must
 * still name itself on that document forever. A deactivated entity mints
 * nothing new. */
export function useSetEntityActive() {
  const invalidate = useInvalidateSettings()

  return useMutation<IssuingEntity, Error, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      api.patch<IssuingEntity>(`/admin/entities/${id}/active`, { isActive }),
    onSuccess: invalidate,
  })
}

export interface SchemePayload {
  /** `null` addresses a company-wide scheme (the client registry), which has
   *  no issuing entity. The server refuses a mismatch either way round — an
   *  entity on a company kind, or none on an entity kind — so this is always
   *  the row's own `entityId`, never a choice. */
  entityId: string | null
  documentKind: NumberedRecordKind
  pattern: string
}

export function useSetNumberingScheme() {
  const invalidate = useInvalidateSettings()

  return useMutation<unknown, Error, SchemePayload>({
    mutationFn: (payload) => api.put('/admin/numbering', payload),
    onSuccess: invalidate,
  })
}

/** Renders what a pattern would produce, without storing it or spending a
 *  number (FR-68).
 *
 * The preview is asked of the SERVER rather than expanded in the browser. A
 * client-side expander is a second implementation of the token language, and
 * the two drift: the founder would then configure a pattern against a preview
 * that disagrees with what the server actually mints. It also means the example
 * carries the real counter value rather than a guessed sequence of 1. */
export function usePreviewNumbering() {
  return useMutation<{ example: string }, Error, SchemePayload>({
    mutationFn: (payload) => api.post<{ example: string }>('/admin/numbering/preview', payload),
  })
}
